import React, { useState } from 'react';
import { Check, Star } from 'lucide-react';

// number/text = limit, `true` = included, `false` = not included.
export const COMPARE = [
  ['Businesses',               1,     2,     4],
  ['Category Listings per Business', 1, 3,   4],
  ['Directory Listing',        true,  true,  true],
  ['Location',                 true,  true,  true],
  ['Contact Information',      true,  true,  true],
  ['Business Photos',          false, 3,     'Unlimited'],
  ['Business Description',     false, true,  true],
  ['Links (Website + Social)', false, 3,     'Unlimited'],
  ['Business Hours',           false, true,  true],
  ['Coupons',                  false, true,  true],
  ['Loyalty Card',             false, true,  true],
  ['PBB Points',               false, true,  true],
  ['CAN Participation',        false, true,  true],
  ['Online Market',            false, true,  true],
  ['Events',                   false, true,  true],
  ['PBB Promotions',           false, true,  true],
  ['Consumer Engagement',      false, true,  true],
  ['Merchant CRM',             false, false, true],
  ['Customer Database',        false, false, true],
  ['Customer Segmentation',    false, false, true],
  ['Customer Activity Tracking', false, false, true],
  ['AI Customer Insights',     false, false, true],
  ['AI Email / SMS',           false, false, true],
  ['Automation',               false, false, true],
  ['Welcome Campaigns',        false, false, true],
  ['Birthday Campaigns',       false, false, true],
  ['Win-Back Campaigns',       false, false, true],
  ['Loyalty Reactivation',     false, false, true],
  ['CAN Customer Tracking',    false, false, true],
  ['Advanced Analytics',       false, false, true],
  ['Custom Sign-up Form',      false, false, true],
  ['Customer Survey',          false, false, true],
];

const DEFAULT_HEADERS = [
  ['$3', 'Category Listing'],
  ['$36', 'Business Growth'],
  ['$120', 'Full Growth Campaign'],
].map(([p, n]) => <>{p}<br/><span className="text-[9px] font-normal normal-case text-gray-500">{n}</span></>);

const THEMES = {
  dark: { wrap: 'pbb-card p-5', head: 'text-gray-500 border-[#1f1f1f]', body: 'divide-[#1a1a1a]', row: 'hover:bg-[#0d1f14]', label: 'text-gray-300', check: 'text-green-400', dash: 'text-gray-600' },
  light: { wrap: 'bg-white rounded-2xl p-5 shadow-lg border border-neutral-200', head: 'text-neutral-500 border-neutral-200', body: 'divide-neutral-100', row: 'hover:bg-green-50', label: 'text-neutral-700', check: 'text-green-600', dash: 'text-neutral-300' },
};

const PopularBadge = () => (
  <div data-testid="most-popular-badge" className="mb-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-yellow-400 text-black text-[10px] uppercase tracking-wider font-bold shadow">
    <Star size={10} className="fill-black"/> Most Popular
  </div>
);

export const PlanCompareTable = ({ theme = 'dark', headers = DEFAULT_HEADERS, title = 'Compare features', popular, tabLabels }) => {
  const t = THEMES[theme];
  const [tab, setTab] = useState(popular ?? 0);
  const popCol = (i) => i === popular ? (theme === 'light' ? 'bg-green-50/70' : 'bg-green-500/5') : '';
  const cell = (v) => {
    if (v === true) return <Check size={14} className={`${t.check} inline`}/>;
    if (v === false) return <span className={t.dash}>—</span>;
    return <span className={`${t.check} font-semibold`}>{v}</span>;
  };
  return (
    <div className={`${t.wrap} overflow-x-auto`} data-testid="pkg-compare">
      <div className="font-semibold mb-3">{title}</div>
      {tabLabels && (
        <div className="md:hidden" data-testid="plan-mobile-switcher">
          <div className="grid grid-cols-3 gap-1 p-1 rounded-full bg-neutral-100" role="tablist">
            {tabLabels.map((l, i) => (
              <button key={l} type="button" role="tab" aria-selected={tab === i} onClick={() => setTab(i)}
                data-testid={`plan-tab-${i}`}
                className={`relative px-2 py-2 rounded-full text-xs font-semibold transition-colors ${tab === i ? 'bg-green-600 text-white shadow' : 'text-neutral-600'}`}>
                {l}
                {i === popular && <Star size={9} className="absolute top-1 right-2 fill-yellow-400 text-yellow-400"/>}
              </button>
            ))}
          </div>
          <div className="mt-4 text-center" data-testid="plan-mobile-header">
            {tab === popular && <PopularBadge/>}
            {headers[tab]}
          </div>
          <ul className={`mt-4 divide-y ${t.body} text-sm`} data-testid="plan-mobile-features">
            {COMPARE.map(([label, ...vals]) => (
              <li key={label} className="flex items-center justify-between py-2">
                <span className={t.label}>{label}</span>{cell(vals[tab])}
              </li>
            ))}
          </ul>
        </div>
      )}
      <table className={`w-full text-sm min-w-[560px] ${tabLabels ? 'hidden md:table' : ''}`}>
        <thead>
          <tr className={`text-[11px] uppercase border-b ${t.head}`}>
            <th className="text-left py-2 pr-4 align-bottom">Feature</th>
            {headers.map((h, i) => (
              <th key={i} className={`py-2 px-2 text-center align-bottom rounded-t-xl ${popCol(i)} ${i === popular ? 'pt-3' : ''}`} data-testid={`compare-col-${i}`}>
                {i === popular && <PopularBadge/>}{h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className={`divide-y ${t.body}`}>
          {COMPARE.map(([label, ...vals]) => (
            <tr key={label} className={t.row}>
              <td className={`py-2 pr-4 ${t.label}`}>{label}</td>
              {vals.map((v, i) => <td key={i} className={`py-2 px-2 text-center ${popCol(i)}`}>{cell(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
